import { Button, Divider, Tooltip, Box } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import JSZip from 'jszip'
import { useContext, useRef } from 'react'
import AppContext from '../../../../context/appContext'
import { useExclusionAreaTypes } from '../../../../hooks/data'
import { getFeaturesFromKML } from '../../../../utils/Files/importMapsHandle'
import { toImportedExclusionArea } from '../toImportedExclusionArea'
import { isEligibilityEnabled } from '../../../../utils/programConfig/areaSubtractions'
import { useCustomSnackbarError } from '../../../../utils/Snackbar/useCustomSnackbarError'
import { MonitoringPanel } from '../../MonitoringPanel'
import { useExclusionAreaContext } from '../ExclusionAreaContext'
import { EligibilityCheckAccordion } from '../../EligibilityCheck/EligibilityCheckAccordion'
import { ImportedExclusionAreasAccordion } from './ImportedExclusionAreasAccordion'
import { ExclusionAreasAccordion } from './ExclusionAreasAccordion'

export const ExclusionAreaPanel = () => {
  const {
    importedExclusionAreas,
    setImportedExclusionAreas,
    isLoading: isLoadingExclusionAreas,
    editingItemId,
    isDrawingMode,
    mapAction,
    canEdit,
    startDrawing,
  } = useExclusionAreaContext()
  const { currentFarm, programConfig, setPartialChanges } = useContext(AppContext)
  const { handleError } = useCustomSnackbarError()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: exclusionAreaTypes, isLoading: isLoadingExclusionAreaTypes } =
    useExclusionAreaTypes({
      programId: currentFarm.programId,
    })

  const processKmlContent = (text: string) => {
    try {
      let exclusionAreasFound = false
      getFeaturesFromKML({
        data: text,
        handleExclusionAreas: features => {
          exclusionAreasFound = true
          const newAreas = features.map(feature =>
            toImportedExclusionArea(feature, { farmId: currentFarm.id, exclusionAreaTypes }),
          )

          setImportedExclusionAreas(prev => [...prev, ...newAreas])
          setPartialChanges(true)
        },
      })

      if (!exclusionAreasFound) {
        handleError(new Error('No se encontraron áreas de exclusión en el archivo.'))
      }
    } catch (error) {
      handleError(error)
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.name.toLowerCase().endsWith('.kmz')) {
      const reader = new FileReader()
      reader.onload = async e => {
        const arrayBuffer = e.target?.result
        if (!arrayBuffer || typeof arrayBuffer === 'string') return

        try {
          const zip = await JSZip.loadAsync(arrayBuffer)
          const kmlFile = Object.values(zip.files).find(f => f.name.endsWith('.kml'))

          if (kmlFile) {
            const text = await kmlFile.async('string')
            processKmlContent(text)
          } else {
            handleError(new Error('No se encontró archivo KML dentro del KMZ.'))
          }
        } catch (error) {
          handleError(error)
        }
      }
      reader.readAsArrayBuffer(file)
    } else {
      const reader = new FileReader()
      reader.onload = async e => {
        const text = e.target?.result
        if (typeof text !== 'string') return
        processKmlContent(text)
      }
      reader.readAsText(file)
    }

    // eslint-disable-next-line no-param-reassign
    event.target.value = ''
  }

  const handleImportKml = () => {
    fileInputRef.current?.click()
  }

  const handleCreateClick = () => {
    startDrawing()
  }

  const isInEditMode = editingItemId !== null || mapAction !== null

  return (
    <MonitoringPanel>
      <input
        ref={fileInputRef}
        accept=".kml,.kmz"
        style={{ display: 'none' }}
        type="file"
        onChange={handleFileChange}
      />
      {!canEdit && !isLoadingExclusionAreas && !isLoadingExclusionAreaTypes && (
        <MonitoringPanel.Alert>
          <MonitoringPanel.Alert.Title>Sección no editable</MonitoringPanel.Alert.Title>
          <MonitoringPanel.Alert.Description>
            No tiene habilitado editar las AE por restricciones en su rol.
          </MonitoringPanel.Alert.Description>
        </MonitoringPanel.Alert>
      )}
      <MonitoringPanel.Description>
        <p>
          Las <b>áreas de exclusión</b> son áreas fuera de proyecto para programa de carbono. <br />
          Al crear el plan, en ellas <b>no será posible colocar sitios aleatorios</b>.
        </p>
      </MonitoringPanel.Description>
      {isEligibilityEnabled(programConfig) && (
        <>
          <EligibilityCheckAccordion />
          <Divider flexItem />
        </>
      )}
      <MonitoringPanel.Title>
        Dibuje y categorice las áreas de exclusión (AE).
      </MonitoringPanel.Title>
      <MonitoringPanel.Description>
        <ul style={{ paddingLeft: 20 }}>
          <li>
            <b>AE “con pastoreo”:</b> Se contemplan para el cálculo de carga animal y en ellas se
            permite colocar sitios de monitoreo manuales
          </li>
          <br />
          <li>
            <b>AE “sin pastoreo”:</b> No se contemplan para el cálculo de carga animal, y en ellas
            no se permite monitorear
          </li>
        </ul>
      </MonitoringPanel.Description>
      <MonitoringPanel.ActionsSection
        direction="row"
        sx={{ justifyContent: 'space-between', width: '100%' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            disabled={
              isInEditMode || isDrawingMode || !canEdit || importedExclusionAreas?.length > 0
            }
            variant="outlined"
            onClick={handleImportKml}
          >
            Importar KML/KMZ
          </Button>
          <Tooltip title="El archivo KML/KMZ debe contener una carpeta específica para áreas de exclusión. Puede utilizar el KML exportado desde la sección 'Establecimiento' como plantilla para agregar sus áreas.">
            <InfoOutlinedIcon color="action" sx={{ fontSize: 20, cursor: 'pointer' }} />
          </Tooltip>
        </Box>
        <MonitoringPanel.CreateButton
          disabled={isInEditMode || isDrawingMode || !canEdit}
          onClick={handleCreateClick}
        >
          Crear área de exclusión
        </MonitoringPanel.CreateButton>
      </MonitoringPanel.ActionsSection>

      {importedExclusionAreas.length > 0 && (
        <ImportedExclusionAreasAccordion
          canEdit={canEdit}
          exclusionAreaTypes={exclusionAreaTypes}
          isDrawingMode={isDrawingMode}
          isInEditMode={isInEditMode}
          isLoading={isLoadingExclusionAreaTypes || isLoadingExclusionAreas}
        />
      )}

      <ExclusionAreasAccordion
        canEdit={canEdit}
        exclusionAreaTypes={exclusionAreaTypes}
        isDrawingMode={isDrawingMode}
        isInEditMode={isInEditMode}
        isLoading={isLoadingExclusionAreaTypes || isLoadingExclusionAreas}
      />
    </MonitoringPanel>
  )
}
